import { importField, importGroup } from "./import";
import { downgradeXdf3Identifier } from "./import/xdf2";
import {
  assert,
  type Field,
  type FieldArbeitskopie,
  type FieldImport,
  type Group,
  type GroupArbeitskopie,
  type GroupImport,
  type Rule,
  type RuleArbeitskopie,
  type RuleReference,
  type Schema,
  type SchemaArbeitskopie,
  type SchemaDocument,
} from "./project";
import { type EditorBackend, SaveStatus } from "./store";
import {
  AbleitungsmodifikationenRepraesentation,
  AbleitungsmodifikationenStruktur,
  DEFAULT_DOCUMENT_PROFILE,
  FreigabeStatus,
} from "./xdatenfelder";

const CURRENT_LOCAL_STORAGE_VERSION = 4;

type Metadata = {
  version: number;
  schemaIdCounter: number;
  groupIdCounter: number;
  fieldIdCounter: number;
  ruleIdCounter: number;
};

export class LocalBackend implements EditorBackend {
  public static init() {
    const backend = new LocalBackend();

    // Completely clear the local storage if the metadata is outdated or missing.
    const backendData = localStorage.getItem("metadata");
    if (
      backendData === null ||
      JSON.parse(backendData).version !== CURRENT_LOCAL_STORAGE_VERSION
    ) {
      backend.reset();
    }

    return backend;
  }

  private getMetadata(): Metadata {
    const metadata = localStorage.getItem("metadata");
    assert(metadata !== null, "Metadata not found");

    return JSON.parse(metadata);
  }

  private saveMetadata(metadata: Metadata) {
    localStorage.setItem("metadata", JSON.stringify(metadata));
  }

  public async importGroup(
    fimId: string,
    fimVersion: string,
  ): Promise<GroupImport> {
    [fimId, fimVersion] = downgradeXdf3Identifier(fimId, fimVersion);
    const groupImport = await importGroup(fimId, fimVersion);

    for (const group of groupImport.groups) {
      this.saveGroup(group);
    }
    for (const field of groupImport.fields) {
      this.saveField(field);
    }
    for (const rule of groupImport.rules) {
      this.saveRule(rule);
    }

    return groupImport;
  }

  public async importField(
    fimId: string,
    fimVersion: string,
  ): Promise<FieldImport> {
    [fimId, fimVersion] = downgradeXdf3Identifier(fimId, fimVersion);
    const fieldImport = await importField(fimId, fimVersion);

    this.saveField(fieldImport.field);

    for (const rule of fieldImport.rules) {
      this.saveRule(rule);
    }

    return fieldImport;
  }

  public createNewSchemaWithFimId(fimId: string): SchemaArbeitskopie {
    const schema: SchemaArbeitskopie = {
      type: "arbeitskopie",
      fimId,
      basicData: {
        name: "Neues Schema",
        beschreibung: undefined,
        definition: undefined,
        normReferences: [],
        releaseStatus: FreigabeStatus.InBearbeitung,
        statusGesetztAm: undefined,
        statusGesetztDurch: undefined,
        gueltigAb: undefined,
        gueltigBis: undefined,
        versionshinweis: undefined,
        veroeffentlichungsdatum: undefined,
        relations: [],
        keywords: [],
      },
      bezeichnung: undefined,
      hilfeText: undefined,
      ableitungsModifikationenRepraesentation:
        AbleitungsmodifikationenRepraesentation.Modifizierbar,
      ableitungsModifikationenStrutkur:
        AbleitungsmodifikationenStruktur.AllesModifizierbar,
      documentSteckbriefFimId: DEFAULT_DOCUMENT_PROFILE,
      children: [],
      rules: [],
    };

    this.saveSchema(schema);
    return schema;
  }

  public createNewGroup(): GroupArbeitskopie {
    const metadata = this.getMetadata();
    metadata.groupIdCounter += 1;
    this.saveMetadata(metadata);

    const idPostfix = metadata.groupIdCounter.toString().padStart(6, "0");
    const fimId = `G99000${idPostfix}`;

    const group: GroupArbeitskopie = {
      type: "arbeitskopie",
      fimId,
      basicData: {
        name: "Neue Gruppe",
        beschreibung: undefined,
        definition: undefined,
        normReferences: [],
        releaseStatus: FreigabeStatus.InBearbeitung,
        statusGesetztAm: undefined,
        statusGesetztDurch: undefined,
        gueltigAb: undefined,
        gueltigBis: undefined,
        versionshinweis: undefined,
        veroeffentlichungsdatum: undefined,
        relations: [],
        keywords: [],
      },
      art: undefined,
      children: [],
      rules: [],
    };

    this.saveGroup(group);
    return group;
  }

  public createNewField(): FieldArbeitskopie {
    const metadata = this.getMetadata();
    metadata.fieldIdCounter += 1;
    this.saveMetadata(metadata);

    const idPostfix = metadata.fieldIdCounter.toString().padStart(6, "0");
    const fimId = `F99000${idPostfix}`;

    const field: FieldArbeitskopie = {
      type: "arbeitskopie",
      fimId,
      inputType: "input",
      basicData: {
        name: "Neues Feld",
        beschreibung: undefined,
        definition: undefined,
        normReferences: [],
        releaseStatus: FreigabeStatus.InBearbeitung,
        statusGesetztAm: undefined,
        statusGesetztDurch: undefined,
        gueltigAb: undefined,
        gueltigBis: undefined,
        versionshinweis: undefined,
        veroeffentlichungsdatum: undefined,
        relations: [],
        keywords: [],
      },
      dataType: "text",
      value: undefined,
      rules: [],
      options: [],
      constraints: {
        minValue: undefined,
        maxValue: undefined,
        minLength: undefined,
        maxLength: undefined,
        pattern: undefined,
      },
    };

    this.saveField(field);
    return field;
  }

  public createNewRule(): RuleArbeitskopie {
    const metadata = this.getMetadata();
    metadata.ruleIdCounter += 1;
    this.saveMetadata(metadata);

    const idPostfix = metadata.ruleIdCounter.toString().padStart(6, "0");
    const fimId = `R99000${idPostfix}`;

    const rule: RuleArbeitskopie = {
      type: "arbeitskopie",
      fimId,

      name: "Neue Regel",
      beschreibung: undefined,
      freitextRegel: undefined,
      bezug: [],
      stichwort: [],
      fachlicherErsteller: undefined,
      letzteAenderung: undefined,

      ruleType: "active",
      skript: undefined,
      expression: undefined,
      target: undefined,
    };

    this.saveRule(rule);
    return rule;
  }

  public hasSchema(fimId: string): boolean {
    return localStorage.getItem(fimId) !== null;
  }

  public saveSchemaDocument(schemaDoc: SchemaDocument) {
    this.saveSchema(schemaDoc.schema);

    for (const group of Object.values(schemaDoc.groups)) {
      this.saveGroup(group);
    }

    for (const field of Object.values(schemaDoc.fields)) {
      this.saveField(field);
    }

    for (const rule of Object.values(schemaDoc.rules)) {
      this.saveRule(rule);
    }
  }

  /**
   * Load all of the necessary date for a schema document from local storage.
   */
  public loadSchemaDocument(
    fimId: string,
    fimVersion?: string,
  ): SchemaDocument {
    const schema = this.loadSchema(fimId, fimVersion);

    const groups: Record<string, Group> = {};
    const fields: Record<string, Field> = {};
    const rules: Record<string, Rule> = {};

    const ruleIdentifiers = new Set<RuleReference>(schema.rules);
    const children = Array.from(schema.children);
    while (children.length > 0) {
      const child = children.pop()!;

      switch (child.type) {
        case "group": {
          if (groups[child.element.fimId] !== undefined) {
            continue;
          }

          const group = this.loadGroup(
            child.element.fimId,
            child.element.fimVersion,
          );

          groups[group.fimId] = group;
          for (const ruleId of group.rules) {
            ruleIdentifiers.add(ruleId);
          }

          children.push(...group.children);
          break;
        }
        case "field": {
          if (fields[child.element.fimId] !== undefined) {
            continue;
          }

          const field = this.loadField(
            child.element.fimId,
            child.element.fimVersion,
          );

          fields[field.fimId] = field;
          for (const ruleId of field.rules) {
            ruleIdentifiers.add(ruleId);
          }

          break;
        }
      }
    }

    for (const ruleId of ruleIdentifiers) {
      let rule: Rule | null = null;
      if (ruleId.fimVersion) {
        rule = this.loadRule(ruleId.fimId, ruleId.fimVersion);
      } else {
        rule = this.loadRule(ruleId.fimId);
      }
      assert(rule !== null);

      rules[rule.fimId] = rule;
    }

    return {
      type: "schema",
      schema,
      groups,
      fields,
      rules,
    };
  }

  public saveSchema(schema: Schema): SaveStatus {
    return this.saveElement(schema);
  }

  public saveGroup(group: Group): SaveStatus {
    return this.saveElement(group);
  }

  public saveField(field: Field): SaveStatus {
    return this.saveElement(field);
  }

  public saveRule(rule: Rule): SaveStatus {
    return this.saveElement(rule);
  }

  private saveElement(element: Schema | Group | Field | Rule): SaveStatus {
    const data = JSON.stringify(element);
    if (element.type === "arbeitskopie") {
      localStorage.setItem(element.fimId, data);
    } else {
      localStorage.setItem(`${element.fimId}V${element.fimVersion}`, data);
    }

    return SaveStatus.SAVED;
  }

  public loadSchema(fimId: string, fimVersion?: string): Schema {
    return this.loadElemenet(fimId, fimVersion);
  }

  public loadGroup(fimId: string, fimVersion?: string): Group {
    return this.loadElemenet(fimId, fimVersion);
  }

  public loadField(fimId: string, fimVersion?: string): Field {
    return this.loadElemenet(fimId, fimVersion);
  }

  public loadRule(fimId: string, fimVersion?: string): Rule {
    return this.loadElemenet(fimId, fimVersion);
  }

  private loadElemenet(fimId: string, fimVersion: string | undefined): any {
    let data: string | null;
    if (fimVersion === undefined) {
      data = localStorage.getItem(fimId);
    } else {
      data = localStorage.getItem(`${fimId}V${fimVersion}`);
    }

    if (data === null) {
      throw new Error(`Could not find element ${fimId} (${fimVersion})`);
    }

    return JSON.parse(data);
  }

  public deleteSchema(fimId: string, fimVersion?: string): void {
    if (fimVersion) {
      localStorage.removeItem(`${fimId}V${fimVersion}`);
    } else {
      localStorage.removeItem(fimId);
    }
  }

  public reset() {
    localStorage.clear();

    this.saveMetadata({
      version: CURRENT_LOCAL_STORAGE_VERSION,
      schemaIdCounter: 0,
      groupIdCounter: 0,
      fieldIdCounter: 0,
      ruleIdCounter: 0,
    });
  }

  public loadSchemas(): Schema[] {
    const schemas: Schema[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)!;
      if (!key.startsWith("S")) {
        continue;
      }

      const schema = JSON.parse(localStorage.getItem(key)!);
      schemas.push(schema);
    }

    return schemas;
  }
}
